import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Check, FileText } from 'lucide-react';
import { draftApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useNotificationStore } from '@/stores';

interface Draft {
  draft_id: string;
  title: string;
  content: string;
  draft_type: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export default function DraftEditor() {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const autoSaveTimeout = useRef<NodeJS.Timeout>();
  const contentRef = useRef(content);
  const titleRef = useRef(title);

  useEffect(() => {
    contentRef.current = content;
    titleRef.current = title;
  }, [content, title]);

  useEffect(() => {
    if (draftId) {
      loadDraft();
    }
  }, [draftId]);

  // Auto-save effect
  useEffect(() => {
    if (!draft || !hasUnsavedChanges) return;

    // Clear existing timeout
    if (autoSaveTimeout.current) {
      clearTimeout(autoSaveTimeout.current);
    }

    // Set new timeout for auto-save (3 seconds after last change)
    autoSaveTimeout.current = setTimeout(() => {
      handleSave(true);
    }, 3000);

    return () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    };
  }, [content, title, hasUnsavedChanges]);

  const loadDraft = async () => {
    if (!draftId) return;

    try {
      setIsLoading(true);
      const response = await draftApi.getDraft(draftId);
      setDraft(response);
      setTitle(response.title);
      setContent(response.content || '');
      setLastSaved(new Date(response.updated_at));
    } catch (error) {
      console.error('Failed to load draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load draft',
        duration: 3000
      });
      navigate('/drafts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isAutoSave: boolean = false) => {
    if (!draftId || isSaving) return;

    try {
      setIsSaving(true);
      await draftApi.updateDraft(draftId, titleRef.current, contentRef.current);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);

      if (!isAutoSave) {
        addNotification({
          type: 'success',
          title: 'Saved',
          message: 'Draft saved successfully',
          duration: 2000
        });
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      if (!isAutoSave) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to save draft',
          duration: 3000
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = useCallback((value: string) => {
    setContent(value);
    setHasUnsavedChanges(true);
  }, []);

  const handleTitleChange = useCallback((value: string) => {
    setTitle(value);
    setHasUnsavedChanges(true);
  }, []);

  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const now = new Date();
    const diff = Math.floor((now.getTime() - lastSaved.getTime()) / 1000);

    if (diff < 60) return 'Saved just now';
    if (diff < 3600) return `Saved ${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `Saved ${Math.floor(diff / 3600)} hours ago`;
    return `Saved on ${lastSaved.toLocaleDateString()}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!draft) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <Button variant="ghost" onClick={() => navigate('/drafts')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex-1 max-w-md">
              <Input
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="text-lg font-semibold border-0 focus-visible:ring-0 px-0"
                placeholder="Untitled Draft"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Save Status */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : hasUnsavedChanges ? (
                <span>Unsaved changes</span>
              ) : (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span>{formatLastSaved()}</span>
                </>
              )}
            </div>

            {/* Manual Save Button */}
            <Button
              onClick={() => handleSave(false)}
              disabled={isSaving || !hasUnsavedChanges}
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Editor Area */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full max-w-7xl mx-auto p-6">
          <div className="h-full bg-white rounded-lg border shadow-sm">
            <Textarea
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              className="w-full h-full resize-none border-0 focus-visible:ring-0 p-6 font-mono text-sm"
              placeholder="Start writing your draft here..."
            />
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="bg-white border-t px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span>{content.length} characters</span>
            <span>{content.split(/\s+/).filter(w => w.length > 0).length} words</span>
          </div>
          <div>
            <span className={`px-2 py-1 rounded ${
              draft.draft_type === 'claims' ? 'bg-purple-100 text-purple-800' :
              draft.draft_type === 'specification' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {draft.draft_type.replace('_', ' ')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
